import { FC } from "react";
import { Heading, Text } from "@otrium/atoms";
import { Box } from "@otrium/core";
import { Trans } from "@lingui/macro";
import isEqual from "lodash-es/isEqual";
import { locale } from "src/config/app-config";
import { formatStreetAddress } from "src/utils/formatStreetAddress";
import { countries } from "src/utils/regionName/countries";
import { EditButtonStyled } from "./CheckoutSummary.styled";
import type { Checkout } from "src/types/forms";

const CheckoutSummary: FC<{
  checkout: Checkout;
  onClickEdit: () => void;
}> = ({ checkout, onClickEdit }) => {
  const isAddressEqual = isEqual(
    checkout.shippingAddress,
    checkout.billingAddress
  );

  return (
    <Box>
      <Box>
        <Heading as="h3" variant="h3">
          <Trans>Contact info</Trans>
        </Heading>

        <Text as="div" mt={3} fontSize={1} lineHeight="24px">
          <Text data-testid="checkout-info-summary-first-and-last-name">
            {checkout.firstName} {checkout.lastName}
          </Text>
          <Text data-testid="checkout-info-summary-email">
            {checkout.email}
          </Text>
          <Text data-testid="checkout-info-summary-phone">
            {checkout.phone}
          </Text>
        </Text>
      </Box>

      <Box>
        <Heading as="h3" variant="h3" mt={4}>
          {isAddressEqual ? (
            <Trans>Shipping & billing address</Trans>
          ) : (
            <Trans>Shipping address</Trans>
          )}
        </Heading>

        <Text as="div" mt={3} fontSize={1} lineHeight="24px">
          <Text data-testid="checkout-info-summary-shipping-address-street">
            {formatStreetAddress(checkout.shippingAddress, locale)}
          </Text>
          <Text data-testid="checkout-info-summary-shipping-address-postal-code-and-city">
            {checkout.shippingAddress.postalCode}{" "}
            {checkout.shippingAddress.city}
          </Text>
          <Text data-testid="checkout-info-summary-shipping-address-country">
            {countries[checkout.shippingAddress.country]}
          </Text>
        </Text>
      </Box>

      {!isAddressEqual && (
        <Box>
          <Heading as="h3" variant="h3" mt={4}>
            <Trans>Billing address</Trans>
          </Heading>

          <Text as="div" mt={3} fontSize={1} lineHeight="24px">
            <Text data-testid="checkout-info-summary-billing-address-street">
              {formatStreetAddress(checkout.billingAddress, locale)}
            </Text>
            <Text data-testid="checkout-info-summary-billing-address-postal-code-and-city">
              {checkout.billingAddress.postalCode}{" "}
              {checkout.billingAddress.city}
            </Text>
            <Text data-testid="checkout-info-summary-billing-address-country">
              {countries[checkout.billingAddress.country]}
            </Text>
          </Text>
        </Box>
      )}

      <Box mt={4}>
        <EditButtonStyled
          data-testid="checkout-info-summary-edit-button"
          onClick={onClickEdit}
        >
          <Text
            as="span"
            display="inline-block"
            fontWeight="600"
            color="tone.black"
            sx={{
              borderBottom: "2px solid black",
            }}
          >
            <Trans>Edit details</Trans>
          </Text>
        </EditButtonStyled>
      </Box>
    </Box>
  );
};

export default CheckoutSummary;
